/* ==========================================================================
   LINDIE Fiction Collection ("See All")

   Two small jobs:
     1. progressively append the rest of the collection as the reader nears the
        end of what is loaded
     2. close an open filter dropdown on outside click / Escape

   Filtering itself is Shopify's own — every control is a link carrying
   url_to_add / url_to_remove — so there is no filter state to manage here.

   Pages come from templates/collection.lfr-grid.liquid, requested against the
   CURRENT url. That matters: the active filter params ride along untouched, so
   a filtered list pages correctly instead of drifting back to the unfiltered
   collection. The cards it returns are the same snippet the first page
   rendered, and the cart / preview handlers are delegated at document
   (lindie-cart.js / lindie-audio-preview.js), so injected cards need no
   re-initialisation.
   ========================================================================== */
(function () {
  'use strict';

  var VIEW = 'lfr-grid';

  /* ── Progressive loading ─────────────────────────────────────────────── */

  function nextUrl(grid) {
    var page = parseInt(grid.getAttribute('data-lfc-next-page'), 10);
    if (isNaN(page) || page < 2) return null;

    /* Start from the current location so every active filter is preserved. */
    var url = new URL(window.location.href);
    url.searchParams.set('view', VIEW);
    url.searchParams.set('page', String(page));
    return url.toString();
  }

  function loadNext(grid) {
    if (grid.dataset.lfcBusy === 'true') return Promise.resolve();

    var url = nextUrl(grid);
    if (!url) return Promise.resolve();

    grid.dataset.lfcBusy = 'true';

    return fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.text();
      })
      .then(function (html) {
        var holder = document.createElement('div');
        holder.innerHTML = html;

        var items = holder.querySelectorAll('.lfc-grid__item');
        var marker = holder.querySelector('[data-lfc-has-more]');
        var hasMore = marker && marker.getAttribute('data-lfc-has-more') === 'true';

        if (items.length) {
          var frag = document.createDocumentFragment();
          /* items is live against `holder`; appendChild moves each node out, so
             walk a static copy. */
          [].slice.call(items).forEach(function (item) { frag.appendChild(item); });
          grid.appendChild(frag);
        }

        if (hasMore && items.length) {
          var page = parseInt(grid.getAttribute('data-lfc-next-page'), 10);
          grid.setAttribute('data-lfc-next-page', String(page + 1));
        } else {
          stop(grid);
        }

        grid.dataset.lfcBusy = '';
      })
      .catch(function () {
        /* Leave the "Load more" link visible so the reader still has a way on. */
        grid.dataset.lfcBusy = '';
        grid.dataset.lfcFailed = 'true';
        var more = document.querySelector('[data-lfc-more]');
        if (more) more.removeAttribute('data-lfc-more-auto');
      });
  }

  function stop(grid) {
    grid.removeAttribute('data-lfc-next-page');
    var more = document.querySelector('[data-lfc-more]');
    if (more) more.remove();
    if (observer) observer.disconnect();
  }

  var observer = null;

  function watch(grid) {
    var more = document.querySelector('[data-lfc-more]');
    if (!grid || !more) return;

    if (!('IntersectionObserver' in window)) return; /* the link still works */

    /* The loader owns the control from here, so hide it without removing it —
       it comes back if a fetch fails. */
    more.setAttribute('data-lfc-more-auto', 'true');

    observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        if (!grid.getAttribute('data-lfc-next-page')) return;

        /* Stop watching while this page is in flight, then watch again once it
           has landed. Re-observing forces a fresh evaluation against the new
           layout: if the sentinel has been pushed below the margin the loader
           goes quiet until the reader scrolls, and if a short page left it on
           screen the observer fires again by itself. Reusing the entry's
           isIntersecting here instead would read a stale snapshot that never
           goes false, and every page would load at once. */
        observer.unobserve(entry.target);

        loadNext(grid).then(function () {
          if (grid.getAttribute('data-lfc-next-page')) observer.observe(entry.target);
        });
      });
    }, { rootMargin: '400px 0px' });

    observer.observe(more);
  }

  /* Clicking the fallback link should append rather than navigate. */
  function bindMoreLink(grid) {
    document.addEventListener('click', function (event) {
      var link = event.target.closest && event.target.closest('[data-lfc-more-link]');
      if (!link || !grid.getAttribute('data-lfc-next-page')) return;
      event.preventDefault();
      loadNext(grid);
    });
  }

  /* ── Filter dropdowns ────────────────────────────────────────────────── */

  /* <details> gives open/close and keyboard support for free; all that is
     missing is closing on an outside click, and one-at-a-time. */
  function bindFields() {
    document.addEventListener('click', function (event) {
      var fields = document.querySelectorAll('[data-lfc-field][open]');
      if (!fields.length) return;

      fields.forEach(function (field) {
        if (!field.contains(event.target)) field.removeAttribute('open');
      });
    });

    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') return;
      var open = document.querySelector('[data-lfc-field][open]');
      if (!open) return;
      open.removeAttribute('open');
      var trigger = open.querySelector('summary');
      if (trigger) trigger.focus();
    });

    /* Opening one closes the others. */
    document.addEventListener('toggle', function (event) {
      var field = event.target;
      if (!field.matches || !field.matches('[data-lfc-field]')) return;
      if (field.open) {
        document.querySelectorAll('[data-lfc-field][open]').forEach(function (other) {
          if (other !== field) other.removeAttribute('open');
        });
      }
      markOpenGroup();
    }, true);
  }

  /* Below 990px the two filter groups scroll sideways, which means overflow-x:
     auto - and the spec makes the other axis clip too. An open dropdown is
     absolutely positioned inside one of those groups, so it was being cut off
     at the row and every click in it landed on the product cards behind.

     The group carrying an open panel is marked here so the CSS can stop
     clipping it. Letting it wrap at the same time keeps the pills from
     spilling sideways out of the page once overflow is visible. */
  function markOpenGroup() {
    var open = document.querySelector('[data-lfc-field][open]');
    var group = open && open.closest ? open.closest('.lfc-filters__group') : null;
    document.querySelectorAll('.lfc-filters__group').forEach(function (g) {
      if (g === group) g.classList.add('is-field-open');
      else g.classList.remove('is-field-open');
    });
  }

  /* ── Filtering in place ──────────────────────────────────────────────── */

  /* Every filter control is still a plain link carrying Shopify's own
     url_to_add / url_to_remove, so this layer only intercepts the navigation —
     with JS off, or if a fetch fails, the same links still work by reloading.

     The grid ELEMENT is reused rather than replaced: bindMoreLink closes over
     it, so swapping the node would leave "Load more" appending into a detached
     grid. Only its contents and its next-page attribute change. */

  var filtersBound = false;
  /* ── Fetching a filtered result ──────────────────────────────────────
     Two things were making a filter click cost about 400ms, measured over six
     rounds against the live store:

       whole response   284 KB
       filter dropdowns 146 KB   (274 options)
       the product grid 2.9 KB   (1 card)

     So the page was being re-rendered and re-sent in full to update one per
     cent of it. Parsing was only 20ms of the total; the rest was the server
     building 274 dropdown options and the wire carrying them.

     Asking the Section Rendering API for this section alone takes the median
     from 400ms to 339ms. It still re-renders the options, so the gain is
     modest — the larger win is the prefetch below, which spends the wait
     before the click rather than after it. */

  function sectionScoped(url) {
    var host = document.querySelector('[data-lfc-section-id]');
    var id = host && host.getAttribute('data-lfc-section-id');
    if (!id) return null;
    try {
      var u = new URL(url, window.location.origin);
      u.searchParams.set('sections', id);
      return { href: u.toString(), id: id };
    } catch (e) {
      return null;
    }
  }

  /* Resolves to a Document for the given url, however it was obtained. Falls back to
     the whole page whenever the section response is not what we expect, so a
     theme change that renames the section cannot break filtering outright. */
  function fetchResults(url) {
    var scoped = sectionScoped(url);
    if (scoped) {
      return fetch(scoped.href)
        .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
        .then(function (json) {
          var html = json && json[scoped.id];
          if (!html) throw new Error('no section in response');
          var doc = new DOMParser().parseFromString(html, 'text/html');
          if (!doc.querySelector('[data-lfc-grid]')) throw new Error('section missing the grid');
          return doc;
        })
        .catch(function () { return fetchWholePage(url); });
    }
    return fetchWholePage(url);
  }

  function fetchWholePage(url) {
    return fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.text(); })
      .then(function (html) { return new DOMParser().parseFromString(html, 'text/html'); });
  }

  /* ── Starting early ──────────────────────────────────────────────────
     A reader pauses over an option before clicking it. That pause is dead time
     today; here it is spent fetching, so by the time the click lands the
     result is usually already in hand.

     Only after a dwell, because a dropdown holds up to a hundred options and
     sliding past them all should not fire a hundred requests. Results are kept
     by URL for the life of the page — a filtered result is not worth
     re-fetching if the reader toggles back to it. */
  var DWELL = 120;
  var prefetched = Object.create(null);
  var dwellTimer = null;

  function prefetch(url) {
    if (!url || prefetched[url]) return;
    prefetched[url] = fetchResults(url).catch(function () {
      delete prefetched[url]; /* a failed guess must not poison the real click */
      return null;
    });
  }

  function bindPrefetch() {
    var start = function (event) {
      if (!event.target.closest) return;
      var link = event.target.closest('[data-lfc-filters] a[href]');
      if (!link || link.hasAttribute('data-lfc-plain-link')) return;
      clearTimeout(dwellTimer);
      dwellTimer = setTimeout(function () { prefetch(link.href); }, DWELL);
    };
    document.addEventListener('pointerover', start);
    document.addEventListener('pointerout', function () { clearTimeout(dwellTimer); });
    /* keyboard users get the same head start on focus */
    document.addEventListener('focusin', start);
  }

  var swapping = false;

  function region() {
    return document.querySelector('[data-lfc-filters]');
  }

  /* ── Tag toggles ──────────────────────────────────────────────────────
     Shopify APPLIES filter.p.tag whether or not a Tags filter is published to
     the theme, but collection.filters only lists the ones it publishes. On this
     store the parameter filters correctly while the filter itself is absent, so
     Liquid cannot produce url_to_add / url_to_remove for it, nor read whether
     it is currently on. That state lives in the query string, which only
     JavaScript can see — so these few functions own it.

     When Shopify does publish the filter, Liquid wins: the pill then carries a
     real url_to_add / url_to_remove and a count, and readState below simply
     agrees with the class Liquid already set. */

  var TAG_PARAM = 'filter.p.tag';

  function tagsIn(url) {
    var raw = url.searchParams.getAll(TAG_PARAM);
    var out = [];
    raw.forEach(function (chunk) {
      chunk.split(',').forEach(function (one) {
        var t = one.trim();
        if (t) out.push(t);
      });
    });
    return out;
  }

  function hasTag(list, tag) {
    var wanted = tag.trim().toLowerCase();
    for (var i = 0; i < list.length; i++) {
      if (list[i].toLowerCase() === wanted) return true;
    }
    return false;
  }

  /* The URL this pill should lead to: the current one with its tag flipped,
     every other parameter left exactly as it is. */
  function urlForTag(tag) {
    var url = new URL(window.location.href);
    var list = tagsIn(url);
    var wanted = tag.trim().toLowerCase();

    var next = list.filter(function (t) { return t.toLowerCase() !== wanted; });
    if (next.length === list.length) next.push(tag);

    url.searchParams.delete(TAG_PARAM);
    if (next.length) url.searchParams.set(TAG_PARAM, next.join(','));

    /* a filter change always restarts paging */
    url.searchParams.delete('page');
    return url.toString();
  }

  /* Paint the pills and the Clear all control from the current URL. */
  function syncFromUrl() {
    var url = new URL(window.location.href);
    var list = tagsIn(url);

    /* Only the fallback pills. Where Shopify published the filter, Liquid
       already set the class from filter_value.active and the href is Shopify's
       own url_to_add / url_to_remove — more canonical than anything built here,
       so it is left alone. */
    document.querySelectorAll('[data-lfc-tag-fallback]').forEach(function (pill) {
      var tag = pill.getAttribute('data-lfc-tag') || '';
      var on = hasTag(list, tag);
      pill.classList.toggle('lfc-toggle--on', on);
      pill.setAttribute('aria-pressed', on ? 'true' : 'false');
      pill.setAttribute('href', urlForTag(tag));
    });

    var anyFilter = false;
    url.searchParams.forEach(function (_value, key) {
      if (key.indexOf('filter.') === 0) anyFilter = true;
    });

    var clear = document.querySelector('[data-lfc-clear-all]');
    if (clear) {
      if (anyFilter) clear.removeAttribute('hidden');
      else clear.setAttribute('hidden', '');
    }

    syncShopAllEscape(url);
  }

  /* ── The way out of an empty genre ──────────────────────────────────
     The empty notice offers the same filters applied to the whole catalogue.
     Liquid renders the bare destination; the filters have to be attached here,
     because the address bar is the only place they can all be read from — a tag
     filter Search & Discovery has not published never reaches collection.filters,
     and Liquid cannot read a query string at all.

     `view` is deliberately not carried: it names the template of the page being
     left, and the destination carries its own. */
  function syncShopAllEscape(url) {
    var link = document.querySelector('[data-lfc-shop-all]');
    if (!link) return;

    var base = link.getAttribute('data-lfc-shop-all-base') || link.getAttribute('href') || '';
    var target;
    try {
      target = new URL(base, window.location.origin);
    } catch (e) {
      return; /* a malformed setting should not take the notice down with it */
    }

    /* Carrying the filters is the useful default — "no new releases here" is
       answered by new releases elsewhere, not by an unfiltered page. It is a
       setting because a merchant may prefer the button to mean "start again". */
    var carry = link.getAttribute('data-lfc-shop-all-carry') !== '0';

    var carried = 0;
    var lastLabel = '';
    url.searchParams.forEach(function (value, key) {
      if (key.indexOf('filter.') !== 0) return;
      carried++;
      lastLabel = labelForFilter(key, value);
      if (carry) target.searchParams.append(key, value);
    });

    /* Hide it only when clicking would change nothing at all.

       Landing on the destination's own page is fine and is the ordinary case
       on Shop All: the filters come off and the full catalogue comes back, so
       the button still does something even though the path is the same. What
       must never happen is a button that rebuilds the exact URL already in the
       address bar.

       So the comparison is path, view AND the filters this link would carry —
       not path and view alone, which hid the button on every Shop All page.
       Filter values are read back off the URL object at both ends, so the two
       sides are encoded the same way and a space as + against %20 cannot make
       two identical URLs look different. */
    var sameTarget =
      target.pathname === window.location.pathname &&
      target.searchParams.get('view') === new URLSearchParams(window.location.search).get('view');

    if (sameTarget) {
      var here = new URLSearchParams(window.location.search);
      var mine = [], theirs = [];
      here.forEach(function (v, k) { if (k.indexOf('filter.') === 0) theirs.push(k + '=' + v); });
      target.searchParams.forEach(function (v, k) { if (k.indexOf('filter.') === 0) mine.push(k + '=' + v); });
      if (mine.sort().join('&') === theirs.sort().join('&')) {
        link.hidden = true;
        return;
      }
    }
    link.hidden = false;

    link.setAttribute('href', target.pathname + target.search + target.hash);

    /* "no new releases in this category" reads better than "no results", but
       only when there is a single filter to name. With two or more, naming one
       of them would be actively misleading. */
    var template = link.getAttribute('data-lfc-shop-all-label') || '';
    if (template.indexOf('{{ filter }}') > -1) {
      link.textContent = carried === 1 && lastLabel
        ? template.replace('{{ filter }}', lastLabel)
        : template.replace('{{ filter }}', 'these filters');
    }
  }

  /* The human name for a filter, taken from the control the reader actually
     used rather than from the parameter, so it matches what is on screen. */
  function labelForFilter(key, value) {
    var pill = document.querySelector('[data-lfc-tag="' + cssEscape(value) + '"]');
    if (pill) return (pill.textContent || '').trim();

    var option = document.querySelector('[data-lfc-filters] a[href*="' + cssEscape(key) + '="][href*="' + cssEscape(encodeURIComponent(value)) + '"]');
    if (option) {
      var text = option.querySelector('.lfc-field__option-label');
      if (text) return (text.textContent || '').trim();
    }
    return value;
  }

  function cssEscape(s) {
    return String(s).replace(/["\\]/g, '\\$&');
  }

  /* Which dropdown was open, so a multi-select stays open across the swap. */
  function openFieldIndex() {
    var fields = document.querySelectorAll('[data-lfc-field]');
    for (var i = 0; i < fields.length; i++) {
      if (fields[i].open) return i;
    }
    return -1;
  }

  function restoreOpenField(index) {
    if (index < 0) return;
    var fields = document.querySelectorAll('[data-lfc-field]');
    if (fields[index]) fields[index].setAttribute('open', '');
  }

  function swap(url, push) {
    if (swapping) return;

    var filters = region();
    var grid = document.querySelector('[data-lfc-grid]');

    /* Never let a click die silently. The caller has already cancelled the
       link's own navigation, so if this page is not shaped the way the swap
       expects, fall back to following it properly rather than doing nothing.
       (Not on popstate — there is no click to honour, and the URL is already
       where it should be.) */
    if (!filters || !grid) {
      if (push) window.location.href = url;
      return;
    }

    swapping = true;
    filters.setAttribute('aria-busy', 'true');
    /* The grid keeps its old results - they stay readable, and blanking them
       makes the page feel slower than it is. It is marked busy so the CSS can
       dim it and show the progress bar. A filter click that was not prefetched
       costs around 400ms, long enough that silence reads as a dead control. */
    grid.setAttribute('aria-busy', 'true');
    var reopen = openFieldIndex();

    /* If the pointer rested on this option long enough the result is already
       here, and the swap is instant. The entry is dropped either way, so a
       stale result can never be reused after the reader filters again. */
    var waiting = prefetched[url];
    delete prefetched[url];

    Promise.resolve(waiting || fetchResults(url))
      .then(function (doc) {
        /* a prefetch that failed resolves to null rather than rejecting, so
           the click still gets a real attempt */
        return doc || fetchResults(url);
      })
      .then(function (doc) {

        var newFilters = doc.querySelector('[data-lfc-filters]');
        var newGrid = doc.querySelector('[data-lfc-grid]');
        if (!newFilters || !newGrid) throw new Error('unexpected response');

        /* filters: safe to replace outright — every handler is delegated */
        filters.innerHTML = newFilters.innerHTML;

        /* grid: same node, new contents, so the loader keeps working */
        if (observer) { observer.disconnect(); observer = null; }
        grid.innerHTML = newGrid.innerHTML;
        grid.dataset.lfcBusy = '';
        grid.dataset.lfcFailed = '';

        var nextPage = newGrid.getAttribute('data-lfc-next-page');
        if (nextPage) {
          grid.setAttribute('data-lfc-next-page', nextPage);
        } else {
          grid.removeAttribute('data-lfc-next-page');
        }

        /* Two siblings of the grid that come and go with the result set. They
           are mutually exclusive — "Load more" only when there are products,
           the empty notice only when there are none — so the same helper can
           place either one after the grid. */
        function syncSibling(selector) {
          var current = document.querySelector(selector);
          var incoming = doc.querySelector(selector);
          if (current && incoming) current.replaceWith(incoming.cloneNode(true));
          else if (current) current.remove();
          else if (incoming) grid.insertAdjacentElement('afterend', incoming.cloneNode(true));
        }

        syncSibling('[data-lfc-more]');
        syncSibling('[data-lfc-empty]');

        if (push) window.history.pushState({ lfc: true }, '', url);

        /* after the URL has moved, so the pills read the state they landed in */
        syncFromUrl();
        restoreOpenField(reopen);
        watch(grid);
      })
      .catch(function () {
        /* Fall back to a normal navigation so the reader is never stuck. */
        window.location.href = url;
      })
      .then(function () {
        swapping = false;
        var current = region();
        if (current) current.removeAttribute('aria-busy');
        var g = document.querySelector('[data-lfc-grid]');
        if (g) g.removeAttribute('aria-busy');
      });
  }

  /* ── Handing the tab choice to the browse page ───────────────────────
     The Fiction / Non-Fiction tabs here are links back to the browsing page,
     and its URL is the same for both — /collections/audiobooks?view=fiction,
     deliberately with no hash. So which tab to open cannot travel in the
     address bar, and Liquid can only read `view` anyway.

     It travels in sessionStorage instead: stored on the way out, read once and
     cleared on arrival by section-lindie-fiction-rows.js. The navigation is NOT
     intercepted — the link works normally, this only leaves a note. */
  function bindTabHandoff() {
    document.addEventListener('click', function (event) {
      if (!event.target.closest) return;
      var tab = event.target.closest('[data-lfr-open-tab]');
      if (!tab) return;

      try {
        window.sessionStorage.setItem('lindie_open_tab', tab.getAttribute('data-lfr-open-tab'));
      } catch (e) {
        /* storage blocked: the browse page opens its default tab, nothing breaks */
      }
    });
  }

  function bindFilters() {
    if (filtersBound) return;
    filtersBound = true;

    document.addEventListener('click', function (event) {
      if (!event.target.closest) return;

      /* The empty state carries its own "Clear all filters" link, and it lives
         outside the filter row — it is a sibling of the grid. Without it here
         that link is the one control on a zero-result page that still does a
         full page reload. */
      var link = event.target.closest('[data-lfc-filters] a[href], [data-lfc-empty] a[href]');
      if (!link) return;

      /* A pill that navigates instead of filtering. Left to the browser: this
         layer would otherwise cancel the click, fetch a page with no filter
         markup, and only reach the destination through the error fallback. */
      if (link.hasAttribute('data-lfc-plain-link')) return;

      /* let modified clicks do what the reader asked (new tab, download, ...) */
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if (event.button !== 0) return;
      if (link.target && link.target !== '_self') return;

      event.preventDefault();
      swap(link.href, true);
    });

    /* Back / forward through filter states. */
    window.addEventListener('popstate', function () {
      if (!document.querySelector('[data-lfc-filters]')) return;
      swap(window.location.href, false);
    });
  }

  function init() {
    if (!document.querySelector('[data-lfc-section]')) return;

    bindFields();
    bindFilters();
    bindTabHandoff();
    bindPrefetch();
    syncFromUrl();

    var grid = document.querySelector('[data-lfc-grid]');
    if (!grid) return;

    bindMoreLink(grid);
    watch(grid);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  /* Theme editor re-renders the section. */
  document.addEventListener('shopify:section:load', function () {
    if (observer) observer.disconnect();
    observer = null;
    init();
    bindSort();
  });

  function bindSort() {
    var sel = document.querySelector('[data-lfc-sort]');
    if (!sel) return;
    sel.addEventListener('change', function () {
      var url = new URL(window.location.href);
      url.searchParams.set('sort_by', this.value);
      window.location.href = url.toString();
    });
  }

  bindSort();
})();
